import React from 'react';
import { X, FileCode, FileJson, FileText, FileType, File } from 'lucide-react';
import { useEditorStore } from '@/store';
import { cn } from '@/lib/utils';
import type { Tab } from '@/types';

function getFileIcon(name: string) {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  switch (ext) {
    case 'ts':
    case 'tsx':
    case 'js':
    case 'jsx':
    case 'sol':
      return <FileCode className="w-3.5 h-3.5" />;
    case 'json':
      return <FileJson className="w-3.5 h-3.5" />;
    case 'md':
    case 'txt':
      return <FileText className="w-3.5 h-3.5" />;
    case 'css':
    case 'html':
      return <FileType className="w-3.5 h-3.5" />;
    default:
      return <File className="w-3.5 h-3.5" />;
  }
}

export default function TabBar() {
  const { tabs, activeTabId, setActiveTab, closeTab } = useEditorStore();

  const handleMouseDown = (e: React.MouseEvent, tab: Tab) => {
    // Middle-click to close
    if (e.button === 1) {
      e.preventDefault();
      closeTab(tab.id);
    }
  };

  if (tabs.length === 0) return null;

  return (
    <div className="flex items-center bg-[#252526] border-b border-[#3c3c3c] overflow-x-auto scrollbar-thin">
      {tabs.map((tab) => (
        <div
          key={tab.id}
          className={cn(
            'group flex items-center gap-1.5 px-3 h-9 text-xs cursor-pointer select-none border-r border-[#3c3c3c] min-w-0 shrink-0',
            tab.id === activeTabId
              ? 'bg-[#1e1e1e] text-white border-t-2 border-t-blue-500'
              : 'text-gray-400 hover:bg-[#2d2d2d]'
          )}
          onClick={() => setActiveTab(tab.id)}
          onMouseDown={(e) => handleMouseDown(e, tab)}
        >
          <span className="text-gray-500">{getFileIcon(tab.name)}</span>
          <span className="truncate max-w-[120px]">{tab.name}</span>
          {tab.isModified && (
            <span className="w-2 h-2 rounded-full bg-blue-400 shrink-0" />
          )}
          <button
            className="ml-1 p-0.5 rounded hover:bg-[#3c3c3c] opacity-0 group-hover:opacity-100 transition-opacity"
            onClick={(e) => {
              e.stopPropagation();
              closeTab(tab.id);
            }}
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ))}
    </div>
  );
}
